import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BadgeIcon from '@mui/icons-material/Badge';
import NotesIcon from '@mui/icons-material/Notes';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
import StarRating from '../../../components/common/StarRating';
import ConfirmModal from '../../../components/portal/common/ConfirmModal';
import { useAppSelector } from '../../../store/hooks';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { apiService } from '../../../services/api';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 3;
  max-width: 900px;
  margin: 0 auto;
  padding: 3rem var(--desktop-page-padding-x, 1.5rem);

  @media (max-width: 768px) {
    padding: 2rem var(--mobile-page-padding-x);
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
  margin-bottom: 2rem;
  padding: 3rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 20px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 2.5rem;
  flex-wrap: wrap;

  .main-info {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }

  .icon-box {
    width: 72px;
    height: 72px;
    border-radius: 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.25);
    svg { font-size: 2.2rem; }
  }

  .title-area {
    h2 { 
      margin: 0; 
      font-size: 2rem; 
      font-weight: 900; 
      color: #111827;
      letter-spacing: -0.02em;
    }
    p { 
      margin: 0.35rem 0 0; 
      color: #64748b; 
      font-weight: 600; 
      display: flex; 
      align-items: center; 
      gap: 0.5rem;
      font-size: 0.9rem;
    }
  }
`;

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.1rem;
  font-weight: 800;
  color: #222;
  margin: 2rem 0 1.25rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(102, 126, 234, 0.1);
  
  &:first-of-type {
    margin-top: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  .label {
    font-size: 0.7rem;
    font-weight: 800;
    color: #64748b;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .value {
    font-size: 1.05rem;
    font-weight: 700;
    color: #222;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.4rem 1rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 800;
  color: #fff;
  text-transform: uppercase;
  background: ${p => {
    switch (p.status) {
      case 'PASSED':
      case 'COMPLETED': return 'linear-gradient(135deg, #11998e, #38ef7d)';
      case 'FAILED':
      case 'CANCELLED': return 'linear-gradient(135deg, #fc4a1a, #f7b733)';
      case 'UPCOMING':
      case 'SCHEDULED': return 'linear-gradient(135deg, #667eea, #764ba2)';
      default: return 'linear-gradient(135deg, #6b7280, #9ca3af)';
    }
  }};
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'success' | 'danger' }>`
  padding: 0.75rem 2rem;
  border-radius: 12px;
  border: none;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: #fff;
  background: ${p => {
    if (p.variant === 'success') return '#34d399';
    if (p.variant === 'danger') return '#ef4444';
    if (p.variant === 'secondary') return '#f1f3f4';
    return '#8b5cf6';
  }};
  ${p => p.variant === 'secondary' && 'color: #444;'}
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    filter: brightness(1.05);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Toast = styled.div<{ variant?: 'error' | 'success' }>`
  position: fixed;
  top: 100px;
  right: 24px;
  z-index: 5000;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  color: #fff;
  font-weight: 700;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  background: ${p => p.variant === 'error' ? 'linear-gradient(135deg, #fc4a1a, #f7b733)' : 'linear-gradient(135deg, #11998e, #38ef7d)'};
`;

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Drawer = styled.div`
  width: 100%;
  max-width: 900px;
  height: auto;
  max-height: 90vh;
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  margin: 2rem;
  border: 1px solid rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    margin: 1rem;
    border-radius: 20px;
    max-height: 95vh;
  }
`;

const DrawerContent = styled.div`
  padding: 2.5rem;
  position: relative;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CloseButton = styled.button`
  display: flex;
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  z-index: 100;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #1e293b;
    transform: rotate(90deg);
  }

  @media (max-width: 768px) {
    top: 0.75rem;
    right: 0.75rem;
    width: 32px;
    height: 32px;
  }
`;

const NewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #f1f5f9;

  .left-side {
    display: flex;
    align-items: center;
    flex: 1;
  }

  .title-info {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .top-row {
    display: grid;
    grid-template-columns: auto 1fr 1.5fr 1fr;
    align-items: flex-start;
    gap: 2rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr 1fr;
      gap: 1.25rem;
    }

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }

  .location-box {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;

    .label {
      font-size: 0.7rem;
      font-weight: 600;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  }

  .date-time-box {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.35rem;
  }

  .time-pill {
    background: #f1f5f9;
    color: #4f46e5;
    padding: 0.25rem 0.75rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 800;
    display: inline-flex;
    align-items: center;
  }

  .date-pill {
    color: #111827;
    font-size: 1rem;
    font-weight: 800;
    white-space: nowrap;
  }

  .location-text {
    color: #111827;
    font-size: 1rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    line-height: 1.4;

    &.clickable {
      cursor: pointer;
      color: #111827;
      transition: all 0.2s;

      &:hover {
        color: #4f46e5;
      }
    }
  }

  .meta-item {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;

    .label {
      font-size: 0.7rem;
      font-weight: 600;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .value {
      font-size: 1rem;
      font-weight: 700;
      color: #111827;

      &.clickable {
        cursor: pointer;
        transition: all 0.2s;

        color: #4f46e5;
        text-decoration: none;

        &:hover {
          color: #4338ca;
          opacity: 0.8;
          text-decoration: underline;
          text-underline-offset: 4px;
        }
      }
    }
  }

  .right-side {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }
`;

const Badge = styled.div<{ variant?: 'success' | 'danger' | 'primary' | 'purple' }>`
  padding: 0.5rem 1.25rem;
  border-radius: 999px;
  font-weight: 800;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  ${p => {
    if (p.variant === 'success') return 'background: #34d399; color: white;';
    if (p.variant === 'danger') return 'background: #ef4444; color: white;';
    if (p.variant === 'purple') return 'background: #8b5cf6; color: white;';
    return 'background: #e5e7eb; color: #374151;';
  }}
`;

const InfoSection = styled.div`
  margin-bottom: 2.5rem;

  .section-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #f1f5f9;

    h3 {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 800;
      color: #111827;
      text-transform: uppercase;
      letter-spacing: 0.025em;
    }
    
    svg {
      font-size: 1.25rem;
      color: #111827;
    }
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 1.25rem;
    }
  }
`;

const InfoItem = styled.div`
  .label {
    font-size: 0.75rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .value {
    font-size: 1.1rem;
    font-weight: 700;
    color: #111827;
  }

  .action-buttons {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }
`;

const MiniButton = styled.button<{ variant?: 'success' | 'danger' }>`
  padding: 0.35rem 0.75rem;
  border-radius: 8px;
  border: none;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  
  ${p => {
    if (p.variant === 'success') return 'background: #34d399; color: white;';
    if (p.variant === 'danger') return 'background: #f87171; color: white;';
    return 'background: #e2e8f0; color: #475569;';
  }}

  &:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
  }
`;

const SectionCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
  transition: all 0.3s ease;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.05);
  }

  .section-header {
    margin-bottom: 1.25rem;
    h3 {
      margin: 0;
      font-size: 1.15rem;
      font-weight: 800;
      color: #111827;
    }
    p {
      margin: 0.25rem 0 0;
      font-size: 0.85rem;
      color: #6b7280;
    }
  }
`;

const Label = styled.div`
  font-size: 0.7rem;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-bottom: 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const Value = styled.div`
  color: #111827;
  font-weight: 700;
  font-size: 1rem;
`;

const NotesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-top: 1rem;
`;

const NoteBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  .note-label {
    font-size: 0.75rem;
    font-weight: 700;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const NoteArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1rem;
  font-family: inherit;
  font-size: 0.95rem;
  color: #1e293b;
  resize: none;
  background: #f8fafc;
  transition: all 0.2s ease;
  line-height: 1.6;

  &:focus {
    outline: none;
    border-color: #6366f1;
    background: white;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
  }

  &:disabled {
    background: #f1f5f9;
    color: #64748b;
    cursor: not-allowed;
    border-color: #e2e8f0;
  }
`;


const PrimaryBtn = styled(ActionButton)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
`;

const SecondaryBtn = styled(ActionButton)`
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
  
  &:hover:not(:disabled) {
    background: #e5e7eb;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: .75rem;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #f1f5f9;
`;

const toTitle = (s?: any) => String(s || '').replace(/[_-]+/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const getStatusBadgeVariant = (status: string): 'success' | 'danger' | 'purple' | 'primary' => {
  const s = String(status || '').toUpperCase();
  if (['PASSED', 'COMPLETED', 'SCHEDULED'].includes(s)) return 'success';
  if (['FAILED', 'CANCELLED'].includes(s)) return 'danger';
  if (['UPCOMING', 'BOOKED', 'IN_PROGRESS'].includes(s)) return 'purple';
  return 'primary';
};

// Type badge for read-only lesson type display
const TypeBadge: React.FC<{ lessonType: string; style?: React.CSSProperties; children?: React.ReactNode }> = ({ lessonType, children, style }) => {
  const t = String(lessonType || 'REGULAR').toUpperCase();
  let color = '#64748b'; // default gray
  let bg = '#f1f5f9';
  if (t === 'PRACTICAL') { color = '#059669'; bg = '#ecfdf5'; }
  else if (t === 'THEORY') { color = '#2563eb'; bg = '#eff6ff'; }
  else if (t === 'EXAM_PREP') { color = '#d97706'; bg = '#fffbeb'; }
  else if (t === 'EXAM') { color = '#dc2626'; bg = '#fef2f2'; }
  else if (t === 'REGULAR') { color = '#4f46e5'; bg = '#eef2ff'; }
  else if (t === 'TRIAL') { color = '#db2777'; bg = '#fdf2f8'; }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.25rem 0.75rem',
        borderRadius: 8,
        fontSize: '0.75rem',
        fontWeight: 800,
        color: color,
        background: bg,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        ...style,
      }}
    >
      {children ?? toTitle(t)}
    </span>
  );
};

const LicenseBadge: React.FC<{ licenseType?: string; style?: React.CSSProperties }> = ({ licenseType, style }) => {
  const { t } = useTranslation();
  if (!licenseType) return null;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
        padding: '0.25rem 0.75rem',
        borderRadius: 8,
        fontSize: '0.75rem',
        fontWeight: 800,
        color: '#dc2626',
        background: '#fef2f2',
        textTransform: 'uppercase',
        ...style,
      }}
    >
      <BadgeIcon style={{ fontSize: '0.9rem' }} />
      {licenseType}
    </span>
  );
};

// Compact grade selector 0-10
const GradeSelector: React.FC<{
  value: number;
  onChange?: (v: number) => void;
  readOnly?: boolean;
}> = ({ value, onChange, readOnly }) => {
  const { t } = useTranslation();
  const options = Array.from({ length: 11 }, (_, i) => i);
  return (
    <div role={readOnly ? 'text' : 'group'} aria-label={String(t('portal.lesson.detail.grade_selector', 'Grade selector'))} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map((g) => (
        <button
          key={g}
          type="button"
          aria-pressed={value === g}
          onClick={() => { if (!readOnly && onChange) onChange(g); }}
          disabled={!!readOnly}
          style={{
            padding: '.3rem .55rem',
            borderRadius: 8,
            border: value === g ? '2px solid #667eea' : '1px solid rgba(0,0,0,0.15)',
            background: value === g ? 'rgba(102,126,234,0.12)' : '#fff',
            fontWeight: 800,
            cursor: readOnly ? 'default' : 'pointer'
          }}
        >
          {g}
        </button>
      ))}
    </div>
  );
};

function mapLesson(raw: any) {
  const l = raw || {};
  const student = l.student || null;
  const teacher = l.teacher || null;
  const studentName = (() => {
    if (l.studentName) return l.studentName;
    if (!student) return undefined;
    const f = String(student.firstName || '').trim();
    const ls = String(student.lastName || '').trim();
    return (!f || !ls) ? (student.username || `${f} ${ls}`.trim()) : `${f} ${ls}`.trim();
  })();
  const teacherName = (() => {
    if (l.teacherName) return l.teacherName;
    if (!teacher) return undefined;
    const f = String(teacher.firstName || '').trim();
    const ls = String(teacher.lastName || '').trim();
    return (!f || !ls) ? (teacher.username || `${f} ${ls}`.trim()) : `${f} ${ls}`.trim();
  })();
  return {
    id: String(l.id ?? l.lessonId ?? ''),
    studentId: student?.id ?? l.studentId ?? null,
    studentUsername: student?.username ?? l.studentUsername ?? undefined,
    studentName,
    teacherId: teacher?.id ?? l.teacherId ?? null,
    teacherUsername: teacher?.username ?? l.teacherUsername ?? undefined,
    teacherName,
    dateTime: l.lessonDateTime ?? l.dateTime,
    duration: l.durationMinutes ?? l.duration ?? 60,
    status: typeof l.isBooked === 'boolean' ? (l.isBooked ? 'SCHEDULED' : 'AVAILABLE') : (l.status ?? 'SCHEDULED'),
    type: l.lessonType ?? l.type ?? 'REGULAR',
    location: l.location ?? l.pickupLocation ?? undefined,
    teacherNote: l.teacherNote ?? undefined,
    studentNote: l.studentNote ?? undefined,
    studentRating: l.studentRating ?? null,
    studentRatingComment: l.studentRatingComment ?? '',
    teacherGrade: l.teacherGrade ?? null,
    teacherGradeComment: l.teacherGradeComment ?? '',
    videoUrl: l.videoUrl ?? '',
    filmingConsent: l.filmingConsent ?? false,
    licenseType: l.licenseType ?? undefined,
  } as any;
}

const UnifiedLessonDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppSelector((s) => s.auth);

  // Normalize role to uppercase without ROLE_ prefix for consistent checks
  const normalizedRole: string = useMemo(() => {
    const raw = String(user?.role || '').toUpperCase();
    return raw.startsWith('ROLE_') ? raw.substring(5) : raw;
  }, [user?.role]);
 
  // Robustly resolve lessonId either from router params or from the current URL path
  const lessonId: string | undefined = useMemo(() => {
    const direct = (params as any)?.lessonId as string | undefined;
    if (direct && String(direct).trim()) return String(direct);
    // Fallback: parse trailing numeric/id segment from pathname
    const path = location?.pathname || '';
    // examples: /portal/teacher/lesson/1171, /portal/admin/lesson/abc123
    const parts = path.split('/').filter(Boolean);
    const last = parts[parts.length - 1];
    return last && last !== 'lesson' ? last : undefined;
  }, [params, location?.pathname]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [lesson, setLesson] = useState<any | null>(null);

  // Teacher actions state
  const [students, setStudents] = useState<Array<{ id: string | number; username?: string; firstName?: string; lastName?: string }>>([]);
  const [studentsLoading, setStudentsLoading] = useState<boolean>(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [assigning, setAssigning] = useState<boolean>(false);
  const [cancelling, setCancelling] = useState<boolean>(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);
  // Edit mode for changing student directly from the detail overview (teacher only)
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<string>('REGULAR');
  const [examType, setExamType] = useState('NORMAL');
  const [licenseType, setLicenseType] = useState('B');
  const [examResult, setExamResult] = useState<boolean | null>(null);

  const [teacherNoteDraft, setTeacherNoteDraft] = useState<string>('');
  const [studentNoteDraft, setStudentNoteDraft] = useState<string>('');


  // Rating/Grading state
  const [studentRating, setStudentRating] = useState<number>(0);
  const [studentRatingComment, setStudentRatingComment] = useState<string>('');
  const [submittingRating, setSubmittingRating] = useState<boolean>(false);

  const [teacherGrade, setTeacherGrade] = useState<number>(0);
  const [teacherGradeComment, setTeacherGradeComment] = useState<string>('');
  const [submittingGrade, setSubmittingGrade] = useState<boolean>(false);
  const [editingStudentRating, setEditingStudentRating] = useState<boolean>(false);
  const [editingTeacherGrade, setEditingTeacherGrade] = useState<boolean>(false);

  const [videoUrlDraft, setVideoUrlDraft] = useState<string>('');
  const [filmingConsentDraft, setFilmingConsentDraft] = useState<boolean>(false);
  const [updatingConsent, setUpdatingConsent] = useState<boolean>(false);

  useEffect(() => {
    loadLesson();
  }, [lessonId]);


  const role: string | undefined = normalizedRole;
  const isStudent = normalizedRole === 'STUDENT';
  const isTeacher = role === 'TEACHER';
  const isAdmin = role === 'ADMIN';
  const [locationDraft, setLocationDraft] = useState<string>('');

  const loadLesson = async () => {
    if (!lessonId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await apiService.get<any>(API_ENDPOINTS.LESSONS.DETAIL(lessonId));
      const raw = (resp && typeof resp === 'object' && 'data' in resp) ? (resp as any).data : resp;
      const mapped = mapLesson(raw);
      setLesson(mapped);
      setSelectedType(String(mapped.type || 'REGULAR'));
      setExamType(mapped.examType || 'NORMAL');
      setLicenseType(mapped.licenseType || 'B');
      setExamResult(mapped.examResult === undefined ? null : mapped.examResult);
      setTeacherNoteDraft(mapped.teacherNote || '');
      setStudentNoteDraft(mapped.studentNote || '');
      setVideoUrlDraft(mapped.videoUrl || '');
      setFilmingConsentDraft(!!mapped.filmingConsent);
      setStudentRating(Number(mapped.studentRating || 0));
      setStudentRatingComment(String(mapped.studentRatingComment || ''));
    } catch (e: any) {
      setError(e?.message || 'Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };


  const handleUpdateConsent = async (consent: boolean) => {
    if (!lesson || !isStudent) return;
    setUpdatingConsent(true);
    try {
      await apiService.patch(API_ENDPOINTS.LESSONS.CONSENT(lesson.id), { consent });
      setLesson({ ...lesson, filmingConsent: consent });
      setFilmingConsentDraft(consent);
      setSuccessMsg(t('portal.lesson.detail.consent_updated', 'Filming consent updated successfully'));
    } catch (e: any) {
      setError(e?.message || 'Failed to update filming consent');
    } finally {
      setUpdatingConsent(false);
    }
  };

  const handleSaveAll = async () => {
    if (!lesson) return;
    setAssigning(true);
    setError(null);
    try {
      const ops = [];
      
      // 1. Update basic lesson info (type, student) if changed
      const sId = selectedStudentId === '' ? null : selectedStudentId;
      const lId = lesson.studentId === null ? null : String(lesson.studentId);
      
      if ((isTeacher || isAdmin) && (String(selectedType) !== String(lesson.type) || String(sId) !== String(lId) || String(licenseType) !== String(lesson.licenseType || 'B'))) {
        const dt = encodeURIComponent(String(lesson.dateTime));
        const dur = encodeURIComponent(String(lesson.duration));
        const type = encodeURIComponent(String(selectedType));
        const lic = encodeURIComponent(String(licenseType));
        const sidParam = sId ? `&studentId=${encodeURIComponent(String(sId))}` : '&studentId=-1';
        ops.push(apiService.post(API_ENDPOINTS.LESSONS.UPDATE(lesson.id) + `?lessonDateTime=${dt}&durationMinutes=${dur}&lessonType=${type}&licenseType=${lic}${sidParam}`));
      }

      // 2. Update exam details if changed
      if ((isTeacher || isAdmin) && (String(examType) !== String(lesson.examType || 'NORMAL') || 
          String(licenseType) !== String(lesson.licenseType || 'B') || 
          examResult !== (lesson.examResult === undefined ? null : lesson.examResult))) {
        ops.push(apiService.patch(`/api/lessons/${lesson.id}/exam-details`, {
          examType,
          licenseType,
          examResult
        }));
      }

      // 3. Update location if changed
      const locationChanged = String(locationDraft) !== String(lesson.location || '');
      const canUpdateLocation = (isTeacher || isAdmin) || (isStudent && lesson.studentId && String(lesson.studentId) === String(user?.id));
      
      if (canUpdateLocation && locationChanged) {
        ops.push(apiService.put(API_ENDPOINTS.LESSONS.PICKUP_LOCATION(lesson.id), { pickupLocation: locationDraft }));
      }

      // 4. Update notes
      const teacherNoteChanged = (isTeacher || isAdmin) && String(teacherNoteDraft) !== String(lesson.teacherNote || '');
      const studentNoteChanged = (isStudent || isTeacher || isAdmin) && String(studentNoteDraft) !== String(lesson.studentNote || '');
      
      if (teacherNoteChanged || studentNoteChanged) {
        const notesPayload: any = {};
        if (teacherNoteChanged) notesPayload.teacherNote = teacherNoteDraft;
        if (studentNoteChanged) notesPayload.studentNote = studentNoteDraft;
        ops.push(apiService.put(API_ENDPOINTS.LESSONS.NOTES(lesson.id), notesPayload));
      }

      // 5. Update rating (student only)
      const ratingChanged = isStudent && (Number(studentRating) !== Number(lesson.studentRating || 0) || String(studentRatingComment) !== String(lesson.studentRatingComment || ''));
      if (ratingChanged) {
        ops.push(apiService.patch(API_ENDPOINTS.LESSONS.RATE(lesson.id), {
          rating: studentRating,
          comment: studentRatingComment
        }));
      }

      // 6. Update grade (teacher only)
      const gradeChanged = (isTeacher || isAdmin) && (Number(teacherGrade) !== Number(lesson.teacherGrade || 0) || String(teacherGradeComment) !== String(lesson.teacherGradeComment || ''));
      if (gradeChanged) {
        ops.push(apiService.patch(API_ENDPOINTS.LESSONS.GRADE(lesson.id), {
          grade: teacherGrade,
          comment: teacherGradeComment
        }));
      }

      // 7. Update video URL (teacher/admin only)
      const videoUrlChanged = (isTeacher || isAdmin) && String(videoUrlDraft) !== String(lesson.videoUrl || '');
      if (videoUrlChanged) {
        ops.push(apiService.patch(API_ENDPOINTS.LESSONS.VIDEO(lesson.id), { videoUrl: videoUrlDraft }));
      }

      await Promise.all(ops);
      setSuccessMsg(t('portal.common.save_success', 'Changes saved successfully'));
      setEditMode(false);
      await loadLesson();
      setTimeout(() => setSuccessMsg(null), 2000);
    } catch (e: any) {
      setError(e?.message || 'Failed to save changes');
    } finally {
      setAssigning(false);
    }
  };

  const hasChanges = useMemo(() => {
    if (!lesson) return false;
    const sId = selectedStudentId === '' ? null : selectedStudentId;
    const lId = lesson.studentId === null ? null : String(lesson.studentId);
    
    const basicChanged = (isTeacher || isAdmin) && (String(selectedType) !== String(lesson.type) || String(sId) !== String(lId) || String(licenseType) !== String(lesson.licenseType || 'B'));
    const examChanged = (isTeacher || isAdmin) && (
      String(examType) !== String(lesson.examType || 'NORMAL') ||
      examResult !== (lesson.examResult === undefined ? null : lesson.examResult)
    );
    const locationChanged = ((isTeacher || isAdmin) || (isStudent && lesson.studentId && String(lesson.studentId) === String(user?.id))) && String(locationDraft) !== String(lesson.location || '');
    const teacherNoteChanged = (isTeacher || isAdmin) && String(teacherNoteDraft) !== String(lesson.teacherNote || '');
    const studentNoteChanged = (isStudent || isTeacher || isAdmin) && String(studentNoteDraft) !== String(lesson.studentNote || '');
    const ratingChanged = isStudent && (Number(studentRating) !== Number(lesson.studentRating || 0) || String(studentRatingComment) !== String(lesson.studentRatingComment || ''));
    const videoUrlChanged = (isTeacher || isAdmin) && String(videoUrlDraft) !== String(lesson.videoUrl || '');

    return basicChanged || examChanged || locationChanged || teacherNoteChanged || studentNoteChanged || ratingChanged || videoUrlChanged;
  }, [lesson, selectedType, selectedStudentId, examType, licenseType, examResult, locationDraft, teacherNoteDraft, studentNoteDraft, isTeacher, isAdmin, isStudent, studentRating, studentRatingComment, videoUrlDraft]);
  const [locationError, setLocationError] = useState<string | null>(null);
  const locationRegex = useMemo(() => /^[\p{L}\d .,'-]+ \d+[A-Za-z]?$/u, []);
  const isOwner = useMemo(() => {
    if (!isTeacher || !lesson) return false;
    const ownsById = String(lesson.teacherId || '') === String(user?.id || '');
    const ownsByUsername = (lesson as any)?.teacherUsername && user?.username && String((lesson as any).teacherUsername) === String(user.username);
    return Boolean(ownsById || ownsByUsername);
  }, [isTeacher, lesson, user?.id, user?.username]);
  // New policy: anyone who can access the detail page may manage booking (assign/cancel)
  const canManageBooking = true;
  const isFuture = useMemo(() => {
    if (!lesson?.dateTime) return false;
    const t = Date.parse(lesson.dateTime);
    return !Number.isNaN(t) && t > Date.now();
  }, [lesson?.dateTime]);

  // Completed override: after 60 minutes from scheduled start, consider completed for UI labelling and editing locks
  const isCompletedWithGrace = useMemo(() => {
    if (!lesson?.dateTime) return false;
    const t = Date.parse(lesson.dateTime);
    if (Number.isNaN(t)) return false;
    const oneHourMs = 60 * 60 * 1000;
    return Date.now() >= t + oneHourMs;
  }, [lesson?.dateTime]);

  // Displayed status: override to COMPLETED if past 60-minute grace from start
  const displayedStatus = useMemo(() => {
    if (!lesson) return '';
    return isCompletedWithGrace ? 'COMPLETED' : String(lesson.status || '');
  }, [lesson, isCompletedWithGrace]);

  const ensureStudentsLoaded = async () => {
    if (!canManageBooking) return;
    if (students.length || studentsLoading) return;
    try {
      setStudentsLoading(true);
      let arr: any[] = [];
      if (isTeacher) {
        // Teacher: only own students
        const resp = await apiService.get<any>('/api/teacher/students');
        const list = (resp && (resp.data || resp.students)) ? (resp.data || resp.students) : resp;
        arr = Array.isArray(list) ? list : [];
      } else if (isAdmin) {
        // Admin: fetch all students via super-admin helper endpoint
        const resp = await apiService.get<any>('/super-admin/api/teachers-and-students');
        // Accept multiple shapes: {data:{students:[]}}, {students:[]}, {data:[...]}, or flat array
        const candidate = (resp && (resp.data?.students ?? resp.students ?? resp.data ?? resp));
        arr = Array.isArray(candidate) ? candidate : [];
      } else {
        // Other roles: no students to manage
        arr = [];
      }
      setStudents(arr);
    } catch (e: any) {
      setError(e?.message || 'Failed to load students');
    } finally {
      setStudentsLoading(false);
    }
  };

  const cancelBooking = async () => {
    if (!lesson) return;
    try {
      setCancelling(true);
      setError(null);
      // Use unified cancel endpoint for all roles to match test expectations
      const url = API_ENDPOINTS.LESSONS.CANCEL(lesson.id);
      await apiService.post<any>(url);
      setSuccessMsg(t('portal.lesson.detail.cancel_success', 'Lesson booking cancelled'));
      setTimeout(() => {
        setSuccessMsg(null);
        goBack();
      }, 1000);
    } catch (e: any) {
      setError(e?.message || t('portal.lesson.detail.cancel_failed', 'Failed to cancel lesson'));
    } finally {
      setCancelling(false);
    }
  };

  const assignStudent = async () => {
    if (!lesson || !selectedStudentId) return;
    try {
      setAssigning(true);
      setError(null);
      if (isTeacher) {
        // Use unified update endpoint for teacher as well, to keep parity with tests
        const dt = encodeURIComponent(String(lesson.dateTime));
        const dur = encodeURIComponent(String(lesson.duration));
        const type = encodeURIComponent(String(lesson.type || 'REGULAR'));
        const sid = encodeURIComponent(String(selectedStudentId));
        await apiService.post<any>(API_ENDPOINTS.LESSONS.UPDATE(lesson.id) + `?lessonDateTime=${dt}&durationMinutes=${dur}&lessonType=${type}&studentId=${sid}`);
      } else {
        const dt = encodeURIComponent(String(lesson.dateTime));
        const dur = encodeURIComponent(String(lesson.duration));
        const type = encodeURIComponent(String(lesson.type || 'REGULAR'));
        const sid = encodeURIComponent(String(selectedStudentId));
        await apiService.post<any>(API_ENDPOINTS.LESSONS.UPDATE(lesson.id) + `?lessonDateTime=${dt}&durationMinutes=${dur}&lessonType=${type}&studentId=${sid}`);
      }
      setSuccessMsg('Student assigned to lesson');
      setTimeout(() => setSuccessMsg(null), 2000);
      setSelectedStudentId('');
      await loadLesson();
    } catch (e: any) {
      setError(e?.message || 'Failed to assign student');
    } finally {
      setAssigning(false);
    }
  };

  // Save changes from inline edit mode (allow reassign without cancel, or unassign)
  const saveStudentChange = async () => {
    if (!lesson) return;
    try {
      setAssigning(true);
      setError(null);
      if (isTeacher) {
        // Align teacher edit mode with expected update endpoint used in tests
        const dt = encodeURIComponent(String(lesson.dateTime));
        const dur = encodeURIComponent(String(lesson.duration));
        const type = encodeURIComponent(String(selectedType || lesson.type || 'REGULAR'));
        const base = `/api/lessons/${lesson.id}/update?lessonDateTime=${dt}&durationMinutes=${dur}&lessonType=${type}`;
        const url = selectedStudentId ? `${base}&studentId=${encodeURIComponent(String(selectedStudentId))}` : base;
        await apiService.post<any>(url);
      } else if (isAdmin) {
        const dt = encodeURIComponent(String(lesson.dateTime));
        const dur = encodeURIComponent(String(lesson.duration));
        const type = encodeURIComponent(String(selectedType || lesson.type || 'REGULAR'));
        const base = `/api/lessons/${lesson.id}/update?lessonDateTime=${dt}&durationMinutes=${dur}&lessonType=${type}`;
        const url = selectedStudentId ? `${base}&studentId=${encodeURIComponent(String(selectedStudentId))}` : base;
        await apiService.post<any>(url);
      } else {
        // Other roles should not hit save; no-op for safety
        return;
      }
      setSuccessMsg(selectedStudentId ? 'Student updated' : 'Student removed');
      setTimeout(() => setSuccessMsg(null), 2000);
      setEditMode(false);
      await loadLesson();
    } catch (e: any) {
      setError(e?.message || 'Failed to update student');
    } finally {
      setAssigning(false);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  const getDayMonth = (dt?: string) => {
    if (!dt) return '—';
    try {
      const date = new Date(dt);
      return date.toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      });
    } catch { return '—'; }
  };

  const getTime = (dt?: string) => {
    if (!dt) return '—';
    try {
      const date = new Date(dt);
      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch { return '—'; }
  };

  return (
    <>
      <ModalBackdrop onClick={(e: React.MouseEvent) => { if (e.target === e.currentTarget) goBack(); }}>
        <Drawer
          role="dialog"
          aria-label={String(t('portal.lesson.detail.drawer_label', 'Lesson details'))}
        >
          <DrawerContent>
            <CloseButton onClick={goBack} aria-label="Close modal">
              <CloseIcon />
            </CloseButton>
            {successMsg && (
              <div style={{ position: 'absolute', top: 24, right: 24, background: '#34d399', color: 'white', padding: '0.5rem 1rem', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, fontWeight: 700 }}>{successMsg}</div>
            )}
            {error && (
              <div style={{ position: 'absolute', top: 24, right: 24, background: '#ef4444', color: 'white', padding: '0.5rem 1rem', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, fontWeight: 700 }}>{error}</div>
            )}

            {!loading && lesson && (
              <>
                <NewHeader>
                  <div className="left-side">
                    <div className="title-info">
                      <div className="top-row">
                        <div className="date-time-box">
                          <div className="date-pill">
                            {getDayMonth(lesson.dateTime)}
                          </div>
                          <div className="time-pill">
                            <ScheduleIcon style={{ fontSize: '0.9rem', marginRight: '0.2rem' }} />
                            {getTime(lesson.dateTime)}
                          </div>
                        </div>

                        <div className="location-box">
                          <span className="label">{t('portal.lesson.detail.location_input_label', 'Location')}</span>
                          <div 
                            className={`location-text ${!editMode && isTeacher && lesson.location ? 'clickable' : ''}`}
                            onClick={() => {
                              if (!editMode && isTeacher && lesson.location) {
                                window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(lesson.location)}`, '_blank');
                              }
                            }}
                          >
                            <LocationOnIcon style={{ fontSize: '1rem', color: '#64748b' }} />
                            {editMode && (isTeacher || isAdmin || (isStudent && lesson.studentId && String(lesson.studentId) === String(user?.id))) ? (
                              <input
                                type="text"
                                value={locationDraft}
                                onChange={(e) => setLocationDraft(e.target.value)}
                                placeholder={String(t('portal.lesson.detail.location_placeholder', 'Enter pick-up location'))}
                                style={{
                                  padding: '0.4rem 0.6rem',
                                  borderRadius: '8px',
                                  border: '1px solid #e2e8f0',
                                  background: 'white',
                                  fontSize: '0.9rem',
                                  fontWeight: 600,
                                  width: '100%'
                                }}
                              />
                            ) : (
                              lesson.location || 'N/A'
                            )}
                          </div>
                        </div>

                        <div className="meta-item">
                          <span className="label">{t('portal.lesson.detail.student_selector', 'Student')}</span>
                          <span 
                            className={`value ${!editMode && isTeacher && lesson.studentId ? 'clickable' : ''}`}
                            onClick={() => {
                              if (!editMode && isTeacher && lesson.studentId) {
                                navigate(`/portal/teacher/student/${lesson.studentId}`);
                              }
                            }}
                          >
                            {editMode && (isTeacher || isAdmin) ? (
                              <select
                                aria-label="Student selector"
                                value={selectedStudentId}
                                onChange={(e) => setSelectedStudentId(e.target.value)}
                                style={{
                                  padding: '0.4rem 0.6rem',
                                  borderRadius: '8px',
                                  border: '1px solid #e2e8f0',
                                  background: 'white',
                                  fontSize: '0.9rem',
                                  fontWeight: 600,
                                  width: '100%'
                                }}
                              >
                                <option value=""></option>
                                {students.map(s => (
                                  <option key={String(s.id)} value={String(s.id)}>
                                    {s.firstName} {s.lastName} ({s.username})
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                <PersonIcon style={{ fontSize: '1rem', color: '#64748b' }} />
                                {lesson.studentName || ''}
                              </div>
                            )}
                          </span>
                        </div>

                        <div className="meta-item">
                          <span className="label">{t('portal.lesson.detail.type_selector', 'Type & License')}</span>
                          <div className="value" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            {editMode && (isTeacher || isAdmin) ? (
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                <select
                                  aria-label="Lesson type"
                                  value={selectedType}
                                  onChange={(e) => setSelectedType(e.target.value)}
                                  style={{
                                    padding: '0.2rem 0.4rem',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0',
                                    background: 'white',
                                    fontSize: '0.85rem',
                                    fontWeight: 700
                                  }}
                                >
                                  <option value="REGULAR">Regular Lesson</option>
                                  <option value="EXAM">Exam</option>
                                  <option value="THEORY">Theory</option>
                                  <option value="TRIAL">Trial</option>
                                  <option value="PRACTICAL">Practical</option>
                                  <option value="EXAM_PREP">Exam Prep</option>
                                </select>
                                
                                <select
                                  aria-label="License type"
                                  value={licenseType}
                                  onChange={(e) => setLicenseType(e.target.value)}
                                  style={{
                                    padding: '0.2rem 0.4rem',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0',
                                    background: 'white',
                                    fontSize: '0.85rem',
                                    fontWeight: 700
                                  }}
                                >
                                  <option value="A">A</option>
                                  <option value="B">B</option>
                                  <option value="BE">BE</option>
                                  <option value="C">C</option>
                                  <option value="D">D</option>
                                  <option value="AM">AM</option>
                                </select>
                              </div>
                            ) : (
                              <span style={{ fontWeight: 800, color: '#111827', fontSize: '0.95rem' }}>
                                {toTitle(lesson.type)}
                                {lesson.licenseType && ` - ${lesson.licenseType}`}
                              </span>
                            )}
                          </div>
                        </div>


                        {lesson.type === 'EXAM' && (
                          <div className="meta-item">
                            <span className="label">Result</span>
                            <div className="value" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <span style={{ color: lesson.examResult === true ? '#34d399' : lesson.examResult === false ? '#ef4444' : 'inherit' }}>
                                {lesson.examResult === true ? 'Passed' : lesson.examResult === false ? 'Failed' : '—'}
                              </span>
                              {(isTeacher || isAdmin) && editMode && (
                                <div style={{ display: 'flex', gap: '0.35rem' }}>
                                  <MiniButton
                                    variant="success"
                                    style={{ padding: '0.2rem 0.4rem', fontSize: '10px', background: examResult === true ? '#34d399' : '#e2e8f0', color: examResult === true ? 'white' : '#475569' }}
                                    onClick={() => setExamResult(true)}
                                  >
                                    Pass
                                  </MiniButton>
                                  <MiniButton
                                    variant="danger"
                                    style={{ padding: '0.2rem 0.4rem', fontSize: '10px', background: examResult === false ? '#ef4444' : '#e2e8f0', color: examResult === false ? 'white' : '#475569' }}
                                    onClick={() => setExamResult(false)}
                                  >
                                    Fail
                                  </MiniButton>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="right-side">
                  </div>
                </NewHeader>

                <InfoSection style={{ display: 'none' }}>
                  <div className="section-header">
                  </div>
                </InfoSection>

                <InfoSection>
                  <NotesGrid>
                    {((isTeacher || isAdmin) || (isStudent && displayedStatus === 'COMPLETED')) && (
                      <NoteBox>
                        <div className="note-label">
                          <NotesIcon fontSize="small" />
                          {t('portal.lesson.detail.teacher_note_label', 'Teacher Note')}
                        </div>
                        <NoteArea
                          id="teacher-note-textarea"
                          aria-label="Teacher Note"
                          placeholder={String(t('portal.lesson.detail.teacher_note_placeholder', "Teacher's note"))}
                          value={teacherNoteDraft}
                          onChange={(e) => setTeacherNoteDraft(e.target.value)}
                          disabled={!editMode || isStudent}
                        />
                      </NoteBox>
                    )}
                    {(displayedStatus === 'COMPLETED' || isTeacher || isAdmin) && (
                      <NoteBox>
                        <div className="note-label">
                          <StarIcon fontSize="small" />
                          {t('portal.lesson.detail.student_review_label', 'Student Review')}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.5rem 0' }}>
                          <StarRating
                            value={studentRating}
                            onChange={(v) => setStudentRating(v)}
                            readOnly={!editMode || (isTeacher || isAdmin)}
                            size={28}
                          />
                          {studentRating > 0 && <span style={{ fontWeight: 800, color: '#111827', fontSize: '1.1rem' }}>{studentRating}/5</span>}
                        </div>
                        <NoteArea
                          id="student-rating-comment"
                          aria-label="Student Review Comment"
                          placeholder={isStudent 
                            ? String(t('portal.lesson.detail.student_review_placeholder_student', 'How was your lesson?'))
                            : String(t('portal.lesson.detail.student_review_placeholder_other', 'No review from student yet.'))}
                          value={studentRatingComment}
                          onChange={(e) => setStudentRatingComment(e.target.value)}
                          disabled={!editMode || (isTeacher || isAdmin)}
                          style={{ minHeight: '100px' }}
                        />
                      </NoteBox>
                    )}
                    <NoteBox>
                      <div className="note-label">
                        <PersonIcon fontSize="small" />
                        <label htmlFor="student-note-textarea">{t('portal.lesson.detail.student_note_label', 'Student Note')}</label>
                      </div>
                      <NoteArea
                        id="student-note-textarea"
                        data-testid="student-note-textarea"
                        aria-label="Student Note"
                        placeholder={String(t('portal.lesson.detail.student_note_placeholder', "Student's note"))}
                        value={studentNoteDraft}
                        onChange={(e) => setStudentNoteDraft(e.target.value)}
                        disabled={!editMode || normalizedRole !== 'STUDENT'}
                      />
                    </NoteBox>
                  </NotesGrid>

                  <NotesGrid>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                      <NoteBox>
                        <div className="note-label">
                          <CameraAltIcon fontSize="small" />
                          {t('portal.lesson.detail.filming_consent', 'Filming Consent')}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0', minHeight: '45px' }}>
                          <input
                            type="checkbox"
                            id="filming-consent"
                            checked={filmingConsentDraft}
                            disabled={updatingConsent || (!isStudent && !editMode)}
                            onChange={(e) => handleUpdateConsent(e.target.checked)}
                            style={{ width: '20px', height: '20px', accentColor: '#6366f1', cursor: (isStudent || editMode) ? 'pointer' : 'default' }}
                          />
                          <label htmlFor="filming-consent" style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', cursor: (isStudent || editMode) ? 'pointer' : 'default' }}>
                            {filmingConsentDraft 
                              ? t('portal.lesson.detail.consent_given', 'Consent Given') 
                              : t('portal.lesson.detail.consent_not_given', 'Consent Not Given')}
                          </label>
                        </div>
                      </NoteBox>

                      <NoteBox>
                        <div className="note-label">
                          <VideoLibraryIcon fontSize="small" />
                          {t('portal.lesson.detail.video_url', 'Lesson Video URL')}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <NoteArea
                            placeholder={String(t('portal.lesson.detail.video_url_placeholder', 'Enter video URL (YouTube, Vimeo, etc.)...'))}
                            value={videoUrlDraft}
                            onChange={(e) => setVideoUrlDraft(e.target.value)}
                            disabled={!(isTeacher || isAdmin) || !editMode}
                            style={{ minHeight: '45px', height: '45px', padding: '0.75rem 1rem' }}
                          />
                        </div>
                        {lesson?.videoUrl && (
                          <div style={{ marginTop: '0.25rem' }}>
                            <a 
                              href={lesson.videoUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#4f46e5', fontWeight: 700, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                            >
                              <VideoLibraryIcon style={{ fontSize: '1rem' }} />
                              {t('portal.lesson.detail.view_video', 'View Lesson Video')}
                            </a>
                          </div>
                        )}
                      </NoteBox>
                    </div>
                  </NotesGrid>
                </InfoSection>

                <FooterActions>
                  {editMode ? (
                    <>
                      <SecondaryBtn onClick={() => setEditMode(false)}>
                        {t('portal.common.cancel', 'Cancel')}
                      </SecondaryBtn>
                      <PrimaryBtn
                        onClick={handleSaveAll}
                        disabled={assigning || !hasChanges}
                        aria-label="Save changes"
                        style={{ background: '#10b981', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)' }}
                      >
                        {assigning ? t('portal.common.saving', 'Saving...') : t('portal.common.save', 'Save')}
                      </PrimaryBtn>
                    </>
                  ) : (
                    <>
                      {(isTeacher || isAdmin || isStudent) && isFuture && (
                        <ActionButton
                          variant="danger"
                          onClick={() => setShowCancelConfirm(true)}
                          style={{ 
                            background: 'transparent', 
                            color: '#ef4444', 
                            border: '1px solid #ef4444',
                            boxShadow: 'none'
                          }}
                        >
                          {isStudent ? t('portal.lesson.detail.cancel_booking', 'Cancel Booking') : t('portal.lesson.detail.cancel_lesson', 'Cancel Lesson')}
                        </ActionButton>
                      )}
                      {(isTeacher || isAdmin || isStudent) && (
                        <PrimaryBtn
                          aria-label="Edit lesson"
                          style={{ background: '#8b5cf6', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)' }}
                          onClick={() => {
                            setEditMode(true);
                            setSelectedType(String(lesson?.type || 'REGULAR'));
                            setLocationDraft(String(lesson?.location || ''));
                            setStudentRating(Number(lesson?.studentRating || 0));
                            setStudentRatingComment(String(lesson?.studentRatingComment || ''));
                            setTeacherGrade(Number(lesson?.teacherGrade || 0));
                            setTeacherGradeComment(String(lesson?.teacherGradeComment || ''));
                            setTeacherNoteDraft(String(lesson?.teacherNote || ''));
                            setStudentNoteDraft(String(lesson?.studentNote || ''));
                            setVideoUrlDraft(String(lesson?.videoUrl || ''));
                            setFilmingConsentDraft(!!lesson?.filmingConsent);
                            if (isTeacher || isAdmin) {
                              setSelectedStudentId(lesson?.studentId ? String(lesson.studentId) : '');
                              ensureStudentsLoaded();
                            }
                          }}
                        >
                          {t('portal.common.edit', 'Edit')}
                        </PrimaryBtn>
                      )}
                    </>
                  )}
                </FooterActions>

                {/* Show info for past unassigned lessons for recordkeeping */}
                {lesson && !isFuture && !lesson.isBooked && !lesson.studentId && (
                  <div style={{
                    background: '#fef9c3',
                    color: '#92400e',
                    borderRadius: 10,
                    padding: '1rem 1.5rem',
                    margin: '1.5rem 0',
                    fontWeight: 700,
                    fontSize: '1rem',
                    boxShadow: '0 2px 8px rgba(251,191,36,0.08)'
                  }}>
                    Assigning a student is allowed for recordkeeping purposes.
                  </div>
                )}
              </>
            )}

            {loading && (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <Value>Loading details...</Value>
              </div>
            )}
          </DrawerContent>
        </Drawer>
      </ModalBackdrop>

      <ConfirmModal
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={cancelBooking}
        title={t('portal.lesson.detail.cancel_booking', 'Cancel booking')}
        message={t('portal.lesson.detail.confirm_cancel_booking', 'Are you sure you want to cancel this lesson?')}
        variant="danger"
      />
    </>
  );
};

export default UnifiedLessonDetail;
